import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "@/common/utils/routes";
import { INiche } from "@/common/services/types";
import { copyNicheLink } from "@/common/utils/share";
import { onCloseNicheMoreMenu, onOpenEditNichePopup } from "@/components/popup-dispatcher/PopupDispatcher";
import ShareIcon from "@/common/images/icons/share_icon.svg?react";
import ManageIcon from "@/common/images/icons/manage_icon.svg?react";
import EditIcon from "@/common/images/icons/edit_icon.svg?react";
import { useUserStore } from "@/common/store/userStore";

interface IUseNicheMoreMenu {
  niche: INiche;
}

export const useNicheMoreMenu = ({
  niche
}: IUseNicheMoreMenu) => {
  const navigate = useNavigate();
  const currentUserName = useUserStore((state) => state.currentUser?.username);

  const isMyNiche = useMemo(() => currentUserName === niche.owner, [currentUserName, niche.owner]);

  const onClose = useCallback(() => {
    onCloseNicheMoreMenu();
  }, []);

  const onCopyNicheLink = useCallback(async () => {
    await copyNicheLink(niche.id);
    onClose();
  }, [niche.id, onClose]);

  const onGoToManage = useCallback(() => {
    navigate(routes.manageProfile());
    onClose();
  }, [navigate, onClose]);

  const onNicheEditOpen = useCallback(() => {
    onOpenEditNichePopup({ niche });
    onClose();
  }, [niche, onClose]);

  const menuItems = useMemo(() => {
    return [
      {
        groupName: null,
        groupMobileName: null,
        items: [
          ...(isMyNiche ? [{
            icon: EditIcon,
            title: "Edit",
            onClick: onNicheEditOpen
          }] : []),
          {
            icon: ShareIcon,
            title: "Share",
            onClick: onCopyNicheLink
          },
          ...(isMyNiche ? [{
            icon: ManageIcon,
            title: "Change niches",
            onClick: onGoToManage,
          }] : []),
        ]
      }
    ];
  }, [isMyNiche, onCopyNicheLink, onGoToManage, onNicheEditOpen]);

  return {
    onClose,
    menuItems,
  };
};
